import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import { Plus, Search, MapPin, Clock, User, MessageSquare, Edit2, Trash2 } from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700', delayed: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700', on_hold: 'bg-gray-100 text-gray-700',
};
const COMPLETION_LABELS = {
  in_progress: 'In Progress', site_complete: 'Site Complete', measurement_pending: 'Meas. Pending',
  measurement_submitted: 'Meas. Submitted', measurement_done: 'Meas. Done', payment_pending: 'Pay. Pending',
  payment_done: 'Pay. Done', photoshoot_pending: 'Photoshoot', fully_complete: 'Complete',
};

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [unreadMap, setUnreadMap] = useState({});
  const { hasPermission, isAdmin } = useAuth();
  const navigate = useNavigate();

  const fetchProjects = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get('/projects', { params });
      setProjects(data.projects);

      // Fetch unread counts per project
      try {
        const { data: unreadData } = await api.get('/projects/unread-counts');
        setUnreadMap(unreadData.unreads || {});
      } catch {}
    } catch { toast.error('Failed to load projects'); }
    setLoading(false);
  };

  useEffect(() => { fetchProjects(); }, [statusFilter]);

  const handleSearch = (e) => { e.preventDefault(); fetchProjects(); };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/projects/${p.id}`); toast.success('Project deleted'); fetchProjects(); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-sm text-gray-500">{projects.length} total projects</p>
        </div>
        {hasPermission('project.create') && (
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium">
            <Plus size={16} /> New Project
          </button>
        )}
      </div>

      <div className="flex gap-3">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none" />
        </form>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
          <option value="">All Status</option>
          <option value="active">Active</option><option value="delayed">Delayed</option>
          <option value="completed">Completed</option><option value="on_hold">On Hold</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border"><p className="text-gray-500">No projects found</p></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => {
            const days = Math.ceil((new Date(p.end_date) - new Date()) / (1000 * 60 * 60 * 24));
            const supervisor = p.assignedUsers?.find(u => u.ProjectAssignment?.is_primary)?.name || p.assignedUsers?.[0]?.name || 'Unassigned';
            const unread = unreadMap[p.id]?.total || 0;
            return (
              <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow relative"
                onClick={() => navigate(`/projects/${p.id}`)}>
                {/* Unread badge */}
                {unread > 0 && (
                  <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unread > 99 ? '99+' : unread}
                  </div>
                )}

                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 truncate flex-1">{p.name}</h3>
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[p.status] || 'bg-gray-100'}`}>{p.status}</span>
                    {hasPermission('project.edit') && (
                      <button onClick={(e) => { e.stopPropagation(); setEditProject(p); }} className="p-1 text-gray-400 hover:text-brand-600 hover:bg-gray-100 rounded"><Edit2 size={12} /></button>
                    )}
                    {hasPermission('project.delete') && (
                      <button onClick={(e) => { e.stopPropagation(); deleteProject(p); }} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={12} /></button>
                    )}
                  </div>
                </div>
                {p.location && <p className="text-xs text-gray-500 flex items-center gap-1 mb-2"><MapPin size={12} />{p.location}</p>}

                {/* Progress bar */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Progress</span>
                    <span className="text-xs font-bold text-brand-600">{p.progress_percentage || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div className="bg-brand-600 h-1.5 rounded-full" style={{ width: `${p.progress_percentage || 0}%` }} />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs flex items-center gap-1 text-gray-500"><User size={12} />{supervisor}</span>
                  <div className="flex items-center gap-2">
                    {unread > 0 && <span className="text-[10px] text-red-500 flex items-center gap-0.5"><MessageSquare size={10} />{unread}</span>}
                    {days > 0 ? (
                      <span className="text-xs font-medium text-green-600 flex items-center gap-1"><Clock size={12} />{days}d left</span>
                    ) : (
                      <span className="text-xs font-medium text-red-600 flex items-center gap-1"><Clock size={12} />{Math.abs(days)}d over</span>
                    )}
                  </div>
                </div>
                {isAdmin && p.budget_amount && (
                  <p className="text-xs text-gray-400 mt-2 border-t pt-2">Budget: ₹{Number(p.budget_amount).toLocaleString('en-IN')} · {p.size_category}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <ProjectModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); fetchProjects(); }} />}
      {editProject && <ProjectModal project={editProject} onClose={() => setEditProject(null)} onSaved={() => { setEditProject(null); fetchProjects(); }} />}
    </div>
  );
}

function ProjectModal({ project, onClose, onSaved }) {
  const isEdit = !!project;
  const [form, setForm] = useState({
    name: project?.name || '', location: project?.location || '',
    start_date: project?.start_date || '', end_date: project?.end_date || '',
    budget_amount: project?.budget_amount || '', carpenter_names: project?.carpenter_names || '',
    description: project?.description || '', status: project?.status || 'active',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (isEdit) { await api.put(`/projects/${project.id}`, form); toast.success('Project updated'); }
      else { await api.post('/projects', form); toast.success('Project created'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Project' : 'New Project'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} required className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input value={form.location} onChange={e => set('location', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} required className="w-full px-3 py-2 border rounded-lg text-sm outline-none" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} required className="w-full px-3 py-2 border rounded-lg text-sm outline-none" /></div>
          </div>
          {isEdit && (
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="active">Active</option><option value="delayed">Delayed</option><option value="on_hold">On Hold</option><option value="completed">Completed</option>
              </select></div>
          )}
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Budget (₹)</label>
            <input type="number" value={form.budget_amount} onChange={e => set('budget_amount', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm outline-none" placeholder="e.g. 3500000" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Carpenters / Laborers</label>
            <input value={form.carpenter_names} onChange={e => set('carpenter_names', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm outline-none" placeholder="Murugan, Senthil" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm outline-none" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? 'Saving...' : isEdit ? 'Update Project' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
